import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import client, { getErrorMessage, getBlobErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { DocumentActionRow } from "@/components/DocumentActionRow";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{value || "—"}</p>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return null;
  return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function EmployeeDetailPanel({ employee: e, siteMap, onClose, onEdit, canManage, onUpdated }) {
  const [uploading, setUploading] = useState(null);
  const [busyDoc, setBusyDoc] = useState(null);
  const [resettingPin, setResettingPin] = useState(false);
  const [manualPin, setManualPin] = useState("");

  const refresh = async () => {
    const res = await client.get(`/employees/${e.employee_id}`);
    onUpdated(res.data);
  };

  const uploadDoc = async (docType, file) => {
    setUploading(docType);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await client.post(`/employees/${e.employee_id}/documents/${docType}`, fd);
      toast.success("Document uploaded");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const viewDocument = async (docType) => {
    try {
      const res = await client.get(`/employees/${e.employee_id}/documents/${docType}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err));
    }
  };

  const deleteDocument = async (docType) => {
    if (!window.confirm("Delete this document? It will be removed from the list.")) return;
    setBusyDoc(docType);
    try {
      await client.delete(`/employees/${e.employee_id}/documents/${docType}`);
      toast.success("Document deleted");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyDoc(null);
    }
  };

  const resetPin = async () => {
    try {
      const res = await client.post(`/employees/${e.employee_id}/reset-pin`, { manual_pin: manualPin || null });
      toast.success(`PIN reset to ${res.data.new_pin}`);
      setResettingPin(false);
      setManualPin("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="employee-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {e.name}
            <Badge variant="outline" className="font-mono">
              {e.employee_id}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            {e.designation} · {e.completeness_status}
          </SheetDescription>
          {canManage && (
            <div className="flex gap-2 pt-2">
              <Button size="sm" data-testid="employee-detail-edit-button" onClick={() => onEdit(e)} className="bg-amber-600 hover:bg-amber-700">
                Edit
              </Button>
              <Button size="sm" variant="outline" data-testid="employee-detail-reset-pin-button" onClick={() => setResettingPin((v) => !v)}>
                Reset PIN
              </Button>
            </div>
          )}
          {resettingPin && (
            <div className="flex items-center gap-2 pt-2">
              <Input
                data-testid="employee-detail-manual-pin-input"
                placeholder="Manual 4-digit PIN (optional)"
                value={manualPin}
                onChange={(ev) => setManualPin(ev.target.value)}
                maxLength={4}
                className="max-w-[220px]"
              />
              <Button size="sm" data-testid="employee-detail-confirm-reset-pin-button" onClick={resetPin}>
                Confirm Reset
              </Button>
            </div>
          )}
        </SheetHeader>

        <Tabs defaultValue="identity" className="mt-4">
          <TabsList>
            <TabsTrigger value="identity" data-testid="employee-tab-identity">
              Identity
            </TabsTrigger>
            <TabsTrigger value="work" data-testid="employee-tab-work">
              Work Assignment
            </TabsTrigger>
            <TabsTrigger value="statutory" data-testid="employee-tab-statutory">
              Statutory & Bank
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="employee-tab-documents">
              Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="identity" className="grid grid-cols-2 gap-4 pt-4">
            <Field label="Employee ID" value={e.employee_id} />
            <Field label="App Role" value={e.app_role || "Not Set"} />
            <Field label="Name" value={e.name} />
            <Field label="Father's Name" value={e.father_name} />
            <Field label="Date of Birth" value={fmtDate(e.dob)} />
            <Field label="Mobile" value={e.mobile} />
            <Field label="Designation" value={e.designation} />
            <Field label="Role Code" value={e.role_code} />
            <div className="col-span-2">
              <Field label="Address" value={e.address} />
            </div>
          </TabsContent>

          <TabsContent value="work" className="grid grid-cols-2 gap-4 pt-4">
            <Field label="Department" value={e.department} />
            <Field label="Location / Site" value={siteMap[e.location_site] || e.location_site} />
            <Field label="Date of Joining" value={fmtDate(e.date_of_joining)} />
            <Field label="Employment Status" value={e.employment_status} />
            <Field label="Salary" value={e.salary ? `₹ ${Number(e.salary).toLocaleString("en-IN")}` : null} />
            <Field label="Assigned Asset(s)" value={(e.asset_ids || []).join(", ")} />
          </TabsContent>

          <TabsContent value="statutory" className="grid grid-cols-2 gap-4 pt-4">
            <Field label="Aadhaar Number" value={e.aadhaar} />
            <Field label="PAN Number" value={e.pan} />
            <Field label="UAN Number" value={e.uan} />
            <Field label="PF Applicable" value={e.pf_applicable ? "Yes" : "No"} />
            <Field label="DL Number" value={e.dl_number} />
            <Field label="DL Type" value={e.dl_type} />
            <Field label="Bank Name" value={e.bank_name} />
            <Field label="Account Number" value={e.account_number} />
            <Field label="IFSC" value={e.ifsc} />
          </TabsContent>

          <TabsContent value="documents" className="pt-4 space-y-2">
            {Object.entries(e.documents || {}).map(([key, doc]) => (
              <div key={key} className="space-y-1">
                {doc.uploaded ? (
                  <DocumentActionRow
                    testId={`employee-doc-${key}`}
                    label={key.replace(/_/g, " ")}
                    filename={doc.filename}
                    meta={doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString("en-IN") : null}
                    canDelete={canManage}
                    busy={busyDoc === key}
                    onView={() => viewDocument(key)}
                    onDelete={() => deleteDocument(key)}
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                    <span className="text-sm capitalize">{key.replace(/_/g, " ")}</span>
                    <Badge variant="outline" className="text-slate-400">
                      Missing
                    </Badge>
                  </div>
                )}
                {canManage && (
                  <label
                    className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${uploading === key ? "text-slate-300" : "text-amber-600 hover:text-amber-700"}`}
                    data-testid={`employee-doc-upload-${key}`}
                  >
                    <Upload className="h-3.5 w-3.5" /> {doc.uploaded ? "Replace" : "Upload"}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      disabled={uploading === key}
                      onChange={(ev) => ev.target.files[0] && uploadDoc(key, ev.target.files[0])}
                    />
                  </label>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
